import React from "react";
import "./price_quote_card.component.scss";
import Modal from "common_components/ui/modal/modal.ui";
import { Assets, Colors, FontFamily, PrimaryButton } from "utils/imports.utils";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import { formatBytesToMB, formatPaiseToRupeesExact } from "utils/functions.utils";
import { STRINGS } from "constants/strings.constant";
import { PriceQuoteCardProps, PriceQuoteTarget, QuoteTargetInfo } from "interfaces/payment_modal.interface";
import ModalCloseButton from "components/modal_close_button/modal_close_button.component";
import PriceQuoteRow from "components/price_quote_row/price_quote_row.component";
import PriceQuoteSummary from "components/price_quote_summary/price_quote_summary.component";

const MODAL_STYLES: React.CSSProperties = { width: 'min(560px, calc(100vw - 32px))', borderRadius: '28px' };

// The quieter lines under a target's label: how many objects sit on it and, when it has one, how big its video is.
const getTargetDetails = (entry: PriceQuoteTarget, info?: QuoteTargetInfo): string[] => {
    const details: string[] = [];
    if (info) details.push(STRINGS.priceQuote.objectCount(info.objectCount));
    if (entry.video_size_bytes) details.push(STRINGS.priceQuote.videoDetail(formatBytesToMB(entry.video_size_bytes) ?? ''));
    return details;
};

// The order summary shown before checkout: the targets being published, the tax in the price, and the total.
const PriceQuoteCard = (props: PriceQuoteCardProps) => {
    const targetCount = props.targetBreakdown.length;
    const firstAmount = targetCount > 0 ? props.targetBreakdown[0].amount : null;
    // "₹129 each" is only true while every target costs the same.
    const eachPrice = firstAmount !== null && props.targetBreakdown.every((entry) => entry.amount === firstAmount)
        ? formatPaiseToRupeesExact(firstAmount)
        : null;
    const subtotal = props.targetBreakdown.reduce((sum, entry) => sum + entry.net_amount, 0);
    const gst = props.targetBreakdown.reduce((sum, entry) => sum + entry.gst_amount, 0);
    const payLabel = STRINGS.priceQuote.pay(formatPaiseToRupeesExact(props.amount));

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose} customStyles={MODAL_STYLES}>
            <div className="price_quote_card">
                <ModalCloseButton onClose={props.onClose} />
                <h2 className="price_quote_card_title">{STRINGS.priceQuote.title}</h2>
                <p className="price_quote_card_description">
                    {props.projectName ? (
                        <>
                            {STRINGS.priceQuote.descriptionPrefix}
                            <strong>{props.projectName}</strong>
                            {STRINGS.priceQuote.descriptionSuffix}
                        </>
                    ) : STRINGS.priceQuote.descriptionNoName}
                </p>
                <div className="price_quote_card_heading">{STRINGS.priceQuote.targetsHeading(targetCount, eachPrice)}</div>
                <div className="price_quote_card_rows">
                    {props.targetBreakdown.map((entry, index) => (
                        <PriceQuoteRow
                            key={entry.target_id}
                            label={STRINGS.priceQuote.targetLabel(index + 1, props.targetInfo?.[entry.target_id]?.name)}
                            imageUrl={props.targetInfo?.[entry.target_id]?.imageUrl}
                            imageAlt={STRINGS.priceQuote.targetImageAlt(index + 1)}
                            details={getTargetDetails(entry, props.targetInfo?.[entry.target_id])}
                            amount={formatPaiseToRupeesExact(entry.amount)}
                        />
                    ))}
                </div>
                <PriceQuoteSummary
                    subtotal={formatPaiseToRupeesExact(subtotal)}
                    gst={formatPaiseToRupeesExact(gst)}
                    total={formatPaiseToRupeesExact(props.amount)}
                />
                <p className="price_quote_card_note">{STRINGS.priceQuote.pricesNote}</p>
                <div className="price_quote_card_actions">
                    <div className="price_quote_card_actions_button">
                        <PrimaryButton
                            text={STRINGS.priceQuote.cancel}
                            type={ButtonTypes.OUTLINED}
                            size={ButtonSize.LARGE}
                            fontFamily={FontFamily.semiBold}
                            color={Colors.primaryTextColor}
                            style={{ border: `1.5px solid ${Colors.primaryTextColor}` }}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                props.onClose();
                            }}
                        />
                    </div>
                    <div className="price_quote_card_actions_button">
                        <PrimaryButton
                            text={payLabel}
                            left_icon={Assets.lock}
                            type={ButtonTypes.PRIMARY}
                            size={ButtonSize.LARGE}
                            fontFamily={FontFamily.semiBold}
                            iconSize={{ width: 16, height: 16 }}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                props.onConfirm(e);
                            }}
                            activity={props.isConfirming}
                        />
                    </div>
                </div>
                <p className="price_quote_card_footnote">{STRINGS.priceQuote.payNote}</p>
            </div>
        </Modal>
    )
}

export default PriceQuoteCard;
